<script setup>
import app from '@/main.js'
import InlineSvg from 'vue-inline-svg'
import { inject, ref, computed } from 'vue'
import ValidationTooltip from '@/components/Functional/ValidationTooltip.vue'
import { useTimeout } from '@vueuse/core'
import validateForm from '@/helpers/validate-form.js'
import { string, object } from 'yup'
import { useAuthStore } from '@/stores/auth.js'
import { authSendLoginLink } from '@/api/api.js'
import ErrorMessage from '@/components/Functional/ErrorMessage.vue'
import helpers from '@/helpers.js'

const close = inject('close')
const authStore = useAuthStore()

const formSchema = object({
  email: string()
    .label(app.$i18n.t('forms.login.labels.email'))
    .email()
    .required()
})

const formValues = ref({
  email: ''
})

const formErrors = ref({
  email: ''
})

const successful = ref(false)
const loading = ref(false)
const errorMessage = ref('')

const { ready, start, stop } = useTimeout(500, { controls: true, immediate: false })

const isLoading = computed(() => {
  return loading.value || !ready.value
})

async function signIn () {
  if (isLoading.value) {
    return
  }

  try {
    await validateForm(formSchema, formValues.value)
  } catch (error) {
    formErrors.value = error

    return
  } 

  loading.value = true
  start()

  try {
    await authSendLoginLink(formValues.value.email)

    errorMessage.value = ''
    successful.value = true

    // props.close()
  } catch (error) {
    errorMessage.value = error?.message ? error.message : 'Unknown error'
    stop()
  } finally {
    loading.value = false
  }
}

const input = ref(null)
</script>

<template>
  <div
    ref="el"
    class="bg-white p-6 shadow-lg w-full relative max-h-[96%] overflow-y-auto thin-scrollbar rounded-t-xl self-end"
    @click.stop>
    <div class="mb-6 flex items-center gap-2 justify-between">
      <h2 class="text-3xl font-bold">
        {{ app.$i18n.t('common.login') }}
      </h2>

      <button
        class="text-gray-950 p-2 -mr-2"
        type="button"
        @click="close">
        <InlineSvg
          :src="require('@/assets/svg/design/cross.svg')"
          class="w-3 h-3"/>
      </button>
    </div>

    <p class="mb-6">
      {{ app.$i18n.t('login.enter_your_email') }}
    </p>

    <form @submit.prevent="signIn">
      <div class="flex items-center justify-between gap-2 mb-10">
        <label
          for="sign-in-email-input"
          class="text-gray-950">
          {{ app.$i18n.t('forms.login.labels.email') }}
        </label>

        <div class="relative w-full max-w-[66%]">
          <input
            id="sign-in-email-input"
            ref="input"
            v-model="formValues.email"
            name="email"
            type="text"
            autocomplete="email"
            :disabled="isLoading || successful"
            :class="{ 'ring-2 ring-inset ring-red-300': formErrors.email }"
            :placeholder="app.$i18n.t('forms.login.placeholders.email')"
            class="w-full px-4 py-3 border-none placeholder-gray-400 bg-gray-100 rounded focus:ring-2 focus:ring-inset focus:ring-blue-300"
            @focus="formErrors.email = ''">

          <ValidationTooltip
            :text="formErrors.email"
            @click="formErrors.email = '';input.focus()"/>
        </div>
      </div>

      <ErrorMessage
        v-if="errorMessage"
        class="-mt-6 mb-4"
        :message="errorMessage"/>

      <div v-if="successful" class="flex gap-2 items-start rounded-lg bg-green-50 border border-green-200 px-4 py-3 mb-4 text-sm text-green-800">
        <InlineSvg
          class="w-4 h-4 shrink-0 mt-0.5 text-green-600"
          :src="require('@/assets/fontawesome/regular/paper-plane-top.svg')"/>
          
        <span>{{ app.$i18n.t('login.email_with_link_sent', { email: formValues.email }) }}</span>
      </div>

      <button
        v-if="!successful"
        :aria-disabled="isLoading || successful"
        :disabled="isLoading || successful"
        :class="{ '!hover:bg-brand-hover !bg-brand-hover cursor-default': isLoading || successful }"
        class="mb-3 px-6 py-3 w-full font-semibold text-white rounded bg-brand hover:bg-brand-hover relative"
        type="submit">
        <span :class="{ 'invisible': isLoading }">
          {{ app.$i18n.t('common.continue') }}
        </span>

        <span
          v-show="isLoading"
          class="w-8 h-8 loader absolute !border-2 inset-0 m-auto"/>
      </button>

      <button
        v-if="!successful"
        type="button"
        class="btn btn-outline btn-block disabled:hover:bg-white"
        @click="close(); authStore.loginIsVisible = true;">
        {{ app.$i18n.t('login.login_with_password') }}
      </button>

      <div class="text-center text-sky-400 text-caption my-3">
        {{ app.$i18n.t('auth.login.dont_have_an_account_yet') }}

        <a
          class="cursor-pointer"
          @click="loginIsVisible = false; close(); helpers.showSignUp()">
          <ins>{{ app.$i18n.t("common.sign_up") }}</ins>
        </a>
      </div>
      
      <div class="text-center text-caption mt-3">
        <a
          href="/terms"
          target="_blank"
          class="text-brand">
          {{ app.$i18n.t('common.terms_of_service') }}
        </a>

        {{ app.$i18n.t('common.and') }}

        <a
          href="/policy"
          target="_blank"
          class="text-brand">
          {{ app.$i18n.t('common.privacy_policy') }}
        </a>
      </div>
    </form>
  </div>
</template>
